import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Save, Plus, Trash2, ListChecks } from "lucide-react";
import { handleSend, handleGet } from "../../../controllers/controller.all";
import { toast } from "react-toastify";
import SubmitButton from "../../../components/ui/SubmitButton";


export default function PlanningCreate() {
    const user = useSelector((state) => state.userReducer?.user);
    const token = user?.access_token || "";
    const ecoleId = user?.ecole?._id || user?.ecole;
    const navigate = useNavigate();

    const [isSubmittingBatch, setIsSubmittingBatch] = useState(false);
    const [classes, setClasses] = useState([]);
    const [matieres, setMatieres] = useState([]);
    const [salles, setSalles] = useState([]);
    const [plannings, setPlannings] = useState([]);

    const [batchList, setBatchList] = useState([]);
    const [fixedClasse, setFixedClasse] = useState("");
    const [isRecurring, setIsRecurring] = useState(false);

    const [formData, setFormData] = useState({
        classe: "",
        matiere: "",
        salle: "",
        heure_debut: "",
        heure_fin: "",
        jour: "",
        date_fin: ""
    });

    useEffect(() => {
        const fetchRefs = async () => {
            await Promise.all([
                handleGet(`/classe/ecole/${ecoleId}`, (clsRes) => {
                    if (clsRes?.classes) setClasses(clsRes.classes.filter(c => c.ecole === ecoleId || c.ecole?._id === ecoleId));
                }, token),
                handleGet(`/matiere/ecole/${ecoleId}`, (matRes) => {
                    if (matRes?.matieres) setMatieres(matRes.matieres.filter(c => c.ecole === ecoleId || c.ecole?._id === ecoleId));
                }, token),
                handleGet(`/salle/ecole/${ecoleId}`, (salleRes) => {
                    if (salleRes?.salles) setSalles(salleRes.salles.filter(c => c.ecole === ecoleId || c.ecole?._id === ecoleId));
                }, token),
                handleGet(`/planning/ecole/${ecoleId}`, (planRes) => {
                    if (planRes?.plannings) setPlannings(planRes.plannings);
                }, token)
            ]);
        };
        if (token) fetchRefs();
    }, [token, ecoleId]);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleFixedClasseChange = (e) => {
        const val = e.target.value;
        setFixedClasse(val);
        setFormData({ ...formData, classe: val });
    };

    const handleAddToBatch = (e) => {
        e.preventDefault();

        const activeClasse = fixedClasse || formData.classe;

        if (!activeClasse || !formData.matiere || !formData.salle || !formData.jour || !formData.heure_debut || !formData.heure_fin) {
            toast.warn("Veuillez remplir tous les champs obligatoires.");
            return;
        }

        if (formData.heure_debut >= formData.heure_fin) {
            toast.warn("L'heure de début doit être avant l'heure de fin.");
            return;
        }

        // Vérification de conflits avec la DB
        const isConflictDB = plannings.some(p => {
            const sameClasse = (p.classe?._id === activeClasse || p.classe === activeClasse);
            const sameJour = (p.jour === formData.jour);
            if (!sameClasse || !sameJour) return false;
            return (formData.heure_debut < p.heure_fin && formData.heure_fin > p.heure_debut);
        });

        // Vérification de conflits dans le batch actuel
        const isConflictBatch = batchList.some(p => {
            const sameClasse = (p.classe === activeClasse);
            const sameJour = (p.jour === formData.jour);
            if (!sameClasse || !sameJour) return false;
            return (formData.heure_debut < p.heure_fin && formData.heure_fin > p.heure_debut);
        });

        if (isConflictDB || isConflictBatch) {
            toast.error("Conflit d'horaire pour cette classe à cette date.");
            return;
        }

        const newEntry = { ...formData, classe: activeClasse };
        setBatchList([...batchList, newEntry]);

        // On reset partiellement le formulaire (on garde la date, la matière et la salle pour enchaîner plus vite)
        setFormData({ ...formData, heure_debut: "", heure_fin: "", classe: fixedClasse ? fixedClasse : formData.classe });
    };

    const handleRemoveFromBatch = (index) => {
        const newList = [...batchList];
        newList.splice(index, 1);
        setBatchList(newList);
    };

    const handleBatchSubmit = async () => {
        if (batchList.length === 0) return;
        setIsSubmittingBatch(true);
        
        let successCount = 0;
        // On envoie chaque requête séquentiellement
        for (const item of batchList) {
            const payload = { ...item, ecole: ecoleId };
            const res = await handleSend(null, "/planning", payload, null, token);
            if (res && res.state === "success") {
                successCount++;
            }
        }
        
        setIsSubmittingBatch(false);
        if (successCount === batchList.length) {
            navigate("/plannings");
        } else {
            toast.warning(`${successCount}/${batchList.length} plannings enregistrés.`);
            navigate("/plannings");
        }
    };

    const getLibelle = (id, list) => {
        const item = list.find(x => x._id === id);
        return item ? item.libelle : "Inconnu";
    };

    return (
        <div className="flex flex-col gap-6 max-w-5xl mx-auto">
            <div className="flex items-center gap-4">
                <Link to="/plannings" className="p-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition text-gray-500">
                    <ArrowLeft className="h-5 w-5" />
                </Link>
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Création par lot de Plannings</h1>
                    <p className="text-sm text-gray-500">Ajoutez plusieurs sessions de cours puis enregistrez-les en une seule fois.</p>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                
                {/* Formulaire d'ajout au lot */}
                <div className="lg:col-span-1 bg-white shadow-sm border border-gray-200 rounded-xl p-5">
                    <h2 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
                        <Plus className="h-5 w-5 text-indigo-500" /> Ajouter une session
                    </h2>

                    <form onSubmit={handleAddToBatch} className="space-y-4">
                        <div className="space-y-2 p-3 bg-indigo-50 rounded-lg border border-indigo-100">
                            <label className="text-sm font-medium text-indigo-900">Classe (Fixe pour ce lot)</label>
                            <select name="fixedClasse" value={fixedClasse} onChange={handleFixedClasseChange} className="w-full border border-indigo-200 bg-white rounded-md py-1.5 px-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500">
                                <option value="">Toutes les classes</option>
                                {classes.map(c => <option key={c._id} value={c._id}>{c.libelle}</option>)}
                            </select>
                            <p className="text-xs text-indigo-700">Sélectionnez une classe ici pour ne pas avoir à la choisir à chaque fois.</p>
                        </div>

                        {!fixedClasse && (
                            <div className="space-y-1">
                                <label className="text-sm font-medium text-gray-700">Classe *</label>
                                <select required name="classe" value={formData.classe} onChange={handleChange} className="w-full border border-gray-300 rounded-lg py-2 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500">
                                    <option value="">Sélectionner</option>
                                    {classes.map(c => <option key={c._id} value={c._id}>{c.libelle}</option>)}
                                </select>
                            </div>
                        )}

                        <div className="space-y-1">
                            <label className="text-sm font-medium text-gray-700">Matière *</label>
                            <select required name="matiere" value={formData.matiere} onChange={handleChange} className="w-full border border-gray-300 rounded-lg py-2 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500">
                                <option value="">Sélectionner</option>
                                {matieres.map(m => <option key={m._id} value={m._id}>{m.libelle}</option>)}
                            </select>
                        </div>

                        <div className="space-y-1">
                            <label className="text-sm font-medium text-gray-700">Salle *</label>
                            <select required name="salle" value={formData.salle} onChange={handleChange} className="w-full border border-gray-300 rounded-lg py-2 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500">
                                <option value="">Sélectionner</option>
                                {salles.map(s => <option key={s._id} value={s._id}>{s.libelle}</option>)}
                            </select>
                        </div>

                        <div className="space-y-2">
                            <div className="flex items-center justify-between">
                                <label className="text-sm font-medium text-gray-700">Date / Jour *</label>
                                <div className="flex items-center gap-2">
                                    <input 
                                        type="checkbox" 
                                        id="isRecurring"
                                        checked={isRecurring}
                                        onChange={(e) => {
                                            setIsRecurring(e.target.checked);
                                            setFormData({ ...formData, jour: "" }); // Reset jour when switching
                                        }}
                                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                    />
                                    <label htmlFor="isRecurring" className="text-xs text-gray-600 cursor-pointer">Cours récurrent</label>
                                </div>
                            </div>
                            
                            {isRecurring ? (
                                <div className="space-y-3">
                                    <select 
                                        required 
                                        name="jour" 
                                        value={formData.jour} 
                                        onChange={handleChange} 
                                        className="w-full border border-gray-300 rounded-lg py-2 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                    >
                                        <option value="">Sélectionner un jour</option>
                                        <option value="lundi">Lundi</option>
                                        <option value="mardi">Mardi</option>
                                        <option value="mercredi">Mercredi</option>
                                        <option value="jeudi">Jeudi</option>
                                        <option value="vendredi">Vendredi</option>
                                        <option value="samedi">Samedi</option>
                                        <option value="dimanche">Dimanche</option>
                                    </select>
                                    
                                    <div className="space-y-1 mt-2">
                                        <label className="text-sm font-medium text-gray-700">Date de fin de récurrence (Optionnel)</label>
                                        <input 
                                            type="date" 
                                            name="date_fin" 
                                            value={formData.date_fin} 
                                            onChange={handleChange} 
                                            className="w-full border border-gray-300 rounded-lg py-2 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" 
                                        />
                                        <p className="text-xs text-gray-500">Laissez vide si ce cours n'a pas de fin prévue.</p>
                                    </div>
                                </div>
                            ) : (
                                <input 
                                    required 
                                    type="date" 
                                    name="jour" 
                                    value={formData.jour} 
                                    onChange={handleChange} 
                                    className="w-full border border-gray-300 rounded-lg py-2 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" 
                                />
                            )}
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1">
                                <label className="text-sm font-medium text-gray-700">Début *</label>
                                <input required type="time" name="heure_debut" value={formData.heure_debut} onChange={handleChange} className="w-full border border-gray-300 rounded-lg py-2 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                            </div>
                            <div className="space-y-1">
                                <label className="text-sm font-medium text-gray-700">Fin *</label>
                                <input required type="time" name="heure_fin" value={formData.heure_fin} onChange={handleChange} className="w-full border border-gray-300 rounded-lg py-2 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                            </div>
                        </div>

                        <button type="submit" className="w-full mt-4 inline-flex justify-center items-center gap-2 px-4 py-2 text-sm font-medium text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-lg hover:bg-indigo-100 transition">
                            <Plus className="h-4 w-4" />
                            Ajouter au lot
                        </button>
                    </form>
                </div>

                {/* Tableau du lot */}
                <div className="lg:col-span-2 bg-white shadow-sm border border-gray-200 rounded-xl flex flex-col">
                    <div className="p-5 border-b border-gray-200 flex justify-between items-center">
                        <h2 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                            <ListChecks className="h-5 w-5 text-emerald-500" /> Lot en attente ({batchList.length})
                        </h2>
                        {batchList.length > 0 && (
                            <SubmitButton 
                                onClick={handleBatchSubmit} 
                                isSubmitting={isSubmittingBatch}
                                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 transition disabled:opacity-50"
                            >
                                <Save className="h-4 w-4" />
                                Tout Enregistrer
                            </SubmitButton>
                        )}
                    </div>

                    <div className="p-0 overflow-x-auto">
                        {batchList.length === 0 ? (
                            <div className="p-8 text-center text-gray-500">
                                Aucune session dans le lot. Remplissez le formulaire à gauche pour commencer.
                            </div>
                        ) : (
                            <table className="w-full text-sm text-left">
                                <thead className="bg-gray-50 text-gray-600 text-xs uppercase font-medium">
                                    <tr>
                                        <th className="px-4 py-3">Classe</th>
                                        <th className="px-4 py-3">Date</th>
                                        <th className="px-4 py-3">Horaire</th>
                                        <th className="px-4 py-3">Matière</th>
                                        <th className="px-4 py-3">Salle</th>
                                        <th className="px-4 py-3 text-right">Action</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {batchList.map((item, index) => (
                                        <tr key={index} className="hover:bg-gray-50">
                                            <td className="px-4 py-3 font-medium text-gray-900">{getLibelle(item.classe, classes)}</td>
                                            <td className="px-4 py-3 text-gray-600">{item.jour}</td>
                                            <td className="px-4 py-3 text-gray-600 font-medium">{item.heure_debut} - {item.heure_fin}</td>
                                            <td className="px-4 py-3 text-gray-600">{getLibelle(item.matiere, matieres)}</td>
                                            <td className="px-4 py-3 text-gray-600">{getLibelle(item.salle, salles)}</td>
                                            <td className="px-4 py-3 text-right">
                                                <button 
                                                    onClick={() => handleRemoveFromBatch(index)}
                                                    className="p-1.5 text-red-500 hover:bg-red-50 rounded-md transition"
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
